import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Button,
} from "@mui/material";
import {
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";

export interface LotManagementRecord {
  data: string;
  tipo_manejo: string;
  tratamento_principal: string;
  realizado_por: string;
  detalhes: {
    produto_comercial?: string;
    dose_aplicada_ml?: number | null;
    custo_do_manejo: number;
  };
}

export interface LotHealthData {
  nome_do_lote: string;
  total_de_animais_no_lote: number;
  manejos_do_lote: LotManagementRecord[];
}

export interface LotHealthHistoryData {
  data: LotHealthData;
  resumo: string;
  url: string;
}

export interface LotHealthHistoryViewProps {
  lotHealthHistoryData: LotHealthHistoryData;
}

const formatDate = (dateString?: string, t?: any): string => {
  if (!dateString) return t ? t('common:date_not_available') : "Data não disponível";
  try {
    // Tenta fazer parse do JSON se for uma string JSON
    const parsed = JSON.parse(dateString);
    if (parsed.formattedDate) {
      return parsed.formattedDate;
    }
  } catch {
    // Se não for JSON, tenta formatar como data normal
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    return new Date(dateString).toLocaleDateString("pt-BR");
  }
  return dateString;
};

const getTreatmentTypeLabel = (tipo: string, t?: any) => {
  if (!t) {
    // Fallback para quando não há tradução disponível
    switch (tipo) {
      case 'registrar_vacinacao':
        return 'Vacinação';
      case 'registrar_medicacao':
        return 'Medicação';
      case 'registrar_exame':
        return 'Exame';
      case 'registrar_cirurgia':
        return 'Cirurgia';
      default:
        return tipo.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  }
  
  switch (tipo) {
    case 'registrar_vacinacao':
      return t('farm:lotHealthHistory.treatmentTypes.vaccination');
    case 'registrar_medicacao':
      return t('farm:lotHealthHistory.treatmentTypes.medication');
    case 'registrar_exame':
      return t('farm:lotHealthHistory.treatmentTypes.examination');
    case 'registrar_cirurgia':
      return t('farm:lotHealthHistory.treatmentTypes.surgery');
    default:
      return tipo.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
};

const getTreatmentTypeColor = (tipo: string) => {
  // Usando a cor padrão escura dos gestores para todos os tipos
  return DESIGN_TOKENS.colors.secondary[700]; // #334155 - cor padrão escura dos gestores
};

export const LotHealthHistoryView: React.FC<LotHealthHistoryViewProps> = ({
  lotHealthHistoryData,
}) => {
  const { t } = useTranslation(['farm', 'common']);
  const { data } = lotHealthHistoryData;
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Extrair registros de manejo do novo formato
  const records = data.manejos_do_lote || [];
  const totalRecords = records.length;
  
  // Informações do lote
  const lotName = data.nome_do_lote || t('farm:lot.unnamed_lot');
  const totalAnimals = data.total_de_animais_no_lote || 0;
  const animalsInLot: string[] = []; // Não há mais lista de animais no novo formato
  
  // Estatísticas de tratamentos
  const treatmentStats: { [key: string]: number } = {};
  records.forEach((record: LotManagementRecord) => {
    treatmentStats[record.tipo_manejo] = (treatmentStats[record.tipo_manejo] || 0) + 1;
  });

  // Paginação
  const totalPages = Math.ceil(records.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentRecords = records.slice(startIndex, startIndex + itemsPerPage);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        minHeight: 0,
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#c1c1c1",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#a8a8a8",
        },
      }}
    >
      <Box sx={{ p: 4 }}>
        {/* Header com informações do lote */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              {t('farm:lotHealthHistory.title', { lotName })}
            </Typography>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <Chip
                label={t('farm:lotHealthHistory.animalsCount', { count: totalAnimals })}
                size="small"
                sx={{
                  bgcolor: DESIGN_TOKENS.colors.neutral[100],
                  color: DESIGN_TOKENS.colors.text.primary,
                  fontFamily: "monospace",
                  fontWeight: 600,
                }}
              />
              <Chip
                label={t('farm:lotHealthHistory.recordsCount', { count: totalRecords })}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>

            {lotHealthHistoryData.resumo && (
              <Typography
                variant="body2"
                sx={{
                  color: DESIGN_TOKENS.colors.text.secondary,
                  fontStyle: "italic",
                }}
              >
                {lotHealthHistoryData.resumo}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Estatísticas */}
        {Object.keys(treatmentStats).length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t('farm:lotHealthHistory.treatmentStatistics')}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {Object.entries(treatmentStats).map(([tipo, count]) => (
                  <Chip
                    key={tipo}
                    label={`${getTreatmentTypeLabel(tipo, t)}: ${count}`}
                    sx={{
                      bgcolor: getTreatmentTypeColor(tipo),
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Animais no lote */}
        {animalsInLot && animalsInLot.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t('farm:lotHealthHistory.animalsInLot', { count: animalsInLot.length })}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {animalsInLot.map((animal: string, index: number) => (
                  <Chip
                    key={index}
                    label={animal}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Histórico de Registros */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t('farm:lotHealthHistory.healthRecords', { count: totalRecords })}
            </Typography>
            
            {currentRecords.length > 0 ? (
              <>
                <Stack spacing={2}>
                  {currentRecords.map((record: LotManagementRecord, index: number) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{
                                bgcolor: getTreatmentTypeColor(record.tipo_manejo),
                                width: 32,
                                height: 32,
                              }}
                            >
                              <img 
                                src="/gestores/seringa.svg" 
                                alt="Seringa" 
                                style={{ 
                                  width: 18, 
                                  height: 18,
                                  filter: 'brightness(0) invert(1)'
                                }} 
                              />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {getTreatmentTypeLabel(record.tipo_manejo)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Realizado por: {record.realizado_por}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            label={formatDate(record.data, t)}
                            size="small"
                            variant="outlined"
                          />
                        </Box>

                        <Stack spacing={1}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {t('farm:lotHealthHistory.mainTreatment')}:
                            </Typography>
                            <Typography variant="body2">
                              {record.tratamento_principal}
                            </Typography>
                          </Box>
                          
                          {record.detalhes.produto_comercial && (
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                {t('farm:lotHealthHistory.commercialProduct')}:
                              </Typography>
                              <Typography variant="body2">
                                {record.detalhes.produto_comercial}
                              </Typography>
                            </Box>
                          )}
                          
                          {record.detalhes.dose_aplicada_ml && (
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                {t('farm:lotHealthHistory.appliedDose')}:
                              </Typography>
                              <Typography variant="body2">
                                {record.detalhes.dose_aplicada_ml} ml
                              </Typography>
                            </Box>
                          )}
                          
                          <Typography variant="body2">
                            <strong>{t('farm:lotHealthHistory.handlingCost')}:</strong> R$ {record.detalhes.custo_do_manejo.toFixed(2)}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>

                {/* Paginação */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      startIcon={<NavigateBefore />}
                    >
                      {t('common:pagination.previous')}
                    </Button>
                    
                    <Typography variant="body2" sx={{ mx: 2 }}>
                      {t('common:pagination.pageOf', { current: currentPage, total: totalPages })}
                    </Typography>
                    
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      endIcon={<NavigateNext />}
                    >
                      {t('common:pagination.next')}
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  {t('farm:lotHealthHistory.noRecordsFound')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LotHealthHistoryView;